/**
 * useCosignContext Hook
 *
 * Derives co-sign workflow context from the current user, the note, and the
 * encounter the note belongs to. Determines whether the current user is the
 * author, the drafter, the encounter's provider or a supervisor, and from
 * that derives which single primary sign affordance the top bar should offer.
 *
 * ---------------------------------------------------------------------------
 * GDN-3911 — why the affordance is derived rather than permission-gated
 * ---------------------------------------------------------------------------
 * `NoteTopBarEdit` used to show an enabled "Sign Note" to anyone holding
 * `create_edit_notes` / `author_supervised_notes` / the supervisor role,
 * regardless of the note's state. A supervisor pressing it on a supervised
 * note still in `reviewing` got a 403 and a generic "Failed to sign note.
 * Please try again." — advice that is actively wrong, because retrying is
 * precisely what cannot help. The affordance now follows note state + viewer.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE CLIENT CAN AND CANNOT KNOW — read before changing this
 * ---------------------------------------------------------------------------
 * Post-GDN-3907, a note drafted by a clinical_staff member on a clinician's
 * encounter carries `documented_by` (the drafter) while `issuing_user` is the
 * encounter's provider and `requires_cosigning` is false. That shape is
 * unambiguous and every row of the issue's table resolves from it directly.
 *
 * LEGACY notes predate that and carry no `documented_by`. A legacy
 * staff-authored note awaiting the supervisor's takeover and a clinician
 * SUPERVISEE's note awaiting its own author's submission are, on the wire,
 * the same shape: `requires_cosigning === true && documented_by == null`.
 * Nothing else on `NoteResponse` separates them — `issuing_user` is a bare
 * `{ name, user_id }` with no role, and the only roster carrying other users'
 * roles (`GET /v2/provider/organizations/members`) is not loaded anywhere on
 * the note route and must not be fetched here.
 *
 * So the legacy ambiguity is resolved by ASKING THE BACKEND, in this order:
 *
 *   1. Before any attempt, offer the enabled action. Blocking a supervisor who
 *      legitimately CAN take over a legacy staff draft — and telling them to
 *      wait for an author who owes nothing — is a worse failure than offering
 *      an action the backend may refuse.
 *   2. When the backend refuses with 403 `author_must_submit_first`, that
 *      answer is definitive: the author is a clinician supervisee who has not
 *      submitted. `useNoteSigning` retains that message for the note and feeds
 *      it back as `submissionBlockedMessage`, which turns the button into the
 *      disabled "Waiting for {author} to submit for co-signature" state using
 *      the backend's own sentence — it names the author, so the client never
 *      has to guess at one.
 *
 * The practical consequence: the disabled row-4 state is reached after one
 * refusal rather than pre-emptively, and only for legacy notes. It is exact
 * from the first render for every note carrying `documented_by`.
 */

import { useMemo } from 'react';
import { useCan, useUser } from '@features/user';
import type { EncounterResponse } from '@store/generated/encounters';
import type { DocumentedByResponse2, NoteResponse } from '@store/generated/notes';

export type CosignStatus = 'none' | 'ready' | 'edits_requested' | 'signed';

/**
 * Which row of the GDN-3911 behaviour table the viewer is looking at. Carried
 * explicitly so stories and tests can assert the branch, not just its copy.
 */
export type SignAffordanceKind =
  /** No sign affordance at all (viewer is neither author nor provider/supervisor). */
  | 'none'
  /** Clinical-staff drafter on a provider's encounter: hand it to the provider. */
  | 'send_to_provider'
  /**
   * Clinical-staff drafter on a note the provider already owns. STATUS ONLY —
   * there is no action here, and offering one is a 403 waiting to happen.
   */
  | 'ready_for_provider'
  /** Provider/supervisor looking at someone else's draft: take it over and sign. */
  | 'review_and_sign'
  /** Supervisor on a supervisee's note the author has not submitted yet. */
  | 'awaiting_author_submission'
  /** Supervisor on a note that IS submitted (`ready`). */
  | 'cosign'
  /** Author addressing requested edits. */
  | 'resubmit'
  /** Ordinary sign with electronic signature. */
  | 'sign'
  /** Ordinary completion, no signature (org signing off, or no patient). */
  | 'complete';

export interface SignAffordance {
  kind: SignAffordanceKind;
  /** Button copy. Empty string when `kind === 'none'`. */
  label: string;
  /** True when the action cannot currently succeed. */
  disabled: boolean;
  /** "Documented by {drafter}" chip shown beside the button, or null. */
  documentedByChip: string | null;
  /** Inline explanation for a disabled affordance, or null. */
  helperText: string | null;
}

export interface CosignContext {
  /** Whether the current user authored this note (is the author of record) */
  isAuthor: boolean;
  /** Whether the current user has the supervisor role */
  isSupervisor: boolean;
  /** Whether the current user has a billing admin role */
  isBillingAdmin: boolean;
  /** Whether co-sign is enabled and this note requires it */
  needsCosign: boolean;
  /** Current co-sign status derived from completion_status */
  cosignStatus: CosignStatus;
  /** The staff member who drafted this note for the author of record, if any. */
  documentedBy: DocumentedByResponse2 | null;
  /** Whether the current user is the drafter named by `documented_by`. */
  isDocumenter: boolean;
  /** Whether the current user is the provider on the note's encounter. */
  isEncounterProvider: boolean;
  /**
   * Whether the documentation was produced by a clinical_staff member rather
   * than by the signing clinician.
   *
   * Definitive when `documented_by` is set. Otherwise PRESUMPTIVE: the legacy
   * `requires_cosigning && !documented_by` shape is shared with a clinician
   * supervisee's note, so a true here does not prove the author is staff. See
   * the module docblock — the backend settles it.
   */
  authorIsClinicalStaff: boolean;
}

const COSIGN_STATUSES = new Set<string>(['ready', 'edits_requested', 'signed']);

/** Statuses on which a provider/supervisor may take over someone else's draft. */
const TAKEOVER_STATUSES = new Set<CosignStatus>(['none', 'edits_requested', 'ready']);

/** The drafter's display name, falling back to something never blank. */
const drafterName = (documentedBy: DocumentedByResponse2 | null): string =>
  documentedBy?.display_name?.trim() || 'clinical staff';

export interface SignAffordanceInput
  extends Pick<
    CosignContext,
    | 'cosignStatus'
    | 'isAuthor'
    | 'isSupervisor'
    | 'isEncounterProvider'
    | 'isDocumenter'
    | 'documentedBy'
    | 'authorIsClinicalStaff'
    | 'needsCosign'
  > {
  /** Whether the viewer holds any note-signing capability at all. */
  canSign: boolean;
  /** The org's note-signing preference. */
  hasNoteSigning: boolean;
  /** Whether the note has a patient assigned. */
  hasPatient: boolean;
  /** The author of record's display name (the provider who signs). */
  authorName: string | null;
  /**
   * The backend's verbatim 403 `author_must_submit_first` message for this
   * note, once it has answered one. Definitive — see the module docblock.
   */
  submissionBlockedMessage: string | null;
}

const NO_AFFORDANCE: SignAffordance = {
  kind: 'none',
  label: '',
  disabled: false,
  documentedByChip: null,
  helperText: null,
};

/**
 * The single primary sign affordance for this viewer on this note.
 *
 * Pure, and exported so the branch table can be unit-tested without mounting
 * the top bar. Order matters: earlier branches are more specific.
 */
export const deriveSignAffordance = ({
  cosignStatus,
  isAuthor,
  isSupervisor,
  isEncounterProvider,
  isDocumenter,
  documentedBy,
  authorIsClinicalStaff,
  needsCosign,
  canSign,
  hasNoteSigning,
  hasPatient,
  authorName,
  submissionBlockedMessage,
}: SignAffordanceInput): SignAffordance => {
  // Viewer is neither author nor provider/supervisor — unchanged: no button.
  if (!canSign) return NO_AFFORDANCE;

  // The backend has told us the author still owes a submission. Definitive,
  // and its sentence names the author, so it is shown rather than rebuilt.
  if (submissionBlockedMessage) {
    return {
      kind: 'awaiting_author_submission',
      label: authorName
        ? `Waiting for ${authorName} to submit for co-signature`
        : 'Waiting for the author to submit for co-signature',
      disabled: true,
      documentedByChip: null,
      helperText: submissionBlockedMessage,
    };
  }

  // The clinical_staff drafter looking at their own work. What they can do
  // depends entirely on who the backend made the AUTHOR OF RECORD.
  if (documentedBy && isDocumenter && cosignStatus !== 'ready') {
    const provider = authorName ?? 'the signing provider';

    // GDN-3907 rule 1: the note is already the clinician's. `issuing_user` is
    // the provider and requires_cosigning is false, so there is no staff
    // transition to make — the physician simply signs. Offering an action here
    // is not merely redundant, it is a guaranteed 403 ("Only the note's author
    // can sign or complete this note"): the drafter is not the author.
    if (!needsCosign) {
      return {
        kind: 'ready_for_provider',
        label: `Ready for ${provider} to sign`,
        disabled: true,
        documentedByChip: null,
        helperText: `This note was documented on the signing provider\u2019s behalf; ${provider} signs it.`,
      };
    }

    // Legacy shape: the staff member IS the author and the note still needs a
    // co-signature, so marking it ready is a real transition they may perform.
    return {
      kind: 'send_to_provider',
      label: `Send to ${provider} for signature`,
      disabled: false,
      documentedByChip: null,
      helperText: null,
    };
  }

  // The provider or supervisor on someone else's draft. Post-GDN-3908 the
  // backend takes the note over and signs it in one step, so one button.
  //
  // A known drafter (`documented_by`) earns the takeover on every takeover
  // status. The LEGACY presumption is deliberately narrower: on `ready` the
  // author has demonstrably submitted, so the ordinary co-sign flow below is
  // correct and must not be hijacked by a guess about who the author is.
  const viewerCanTakeOver = isEncounterProvider || isSupervisor;
  const isStaffDraft = documentedBy
    ? TAKEOVER_STATUSES.has(cosignStatus)
    : authorIsClinicalStaff && !isAuthor && cosignStatus !== 'ready';
  if (isStaffDraft && !isDocumenter && viewerCanTakeOver) {
    return {
      kind: 'review_and_sign',
      label: 'Review & Sign',
      disabled: false,
      // Only claim a drafter we actually know. The legacy shape has none.
      documentedByChip: documentedBy ? `Documented by ${drafterName(documentedBy)}` : null,
      helperText: null,
    };
  }

  // Author addressing requested edits — unchanged behaviour.
  if (cosignStatus === 'edits_requested') {
    return {
      kind: 'resubmit',
      label: 'Resubmit for Co-sign',
      disabled: false,
      documentedByChip: null,
      helperText: null,
    };
  }

  // Submitted and awaiting a supervisor — unchanged.
  if (cosignStatus === 'ready') {
    return {
      kind: 'cosign',
      label: 'Co-sign Note',
      disabled: false,
      documentedByChip: null,
      helperText: null,
    };
  }

  // Ordinary finalize — unchanged.
  return hasNoteSigning && hasPatient
    ? { kind: 'sign', label: 'Sign Note', disabled: false, documentedByChip: null, helperText: null }
    : {
        kind: 'complete',
        label: 'Complete Note',
        disabled: false,
        documentedByChip: null,
        helperText: null,
      };
};

/** "Documented by {drafter} · {role}" for the note header, or null. */
export const formatDocumentedBy = (
  documentedBy: DocumentedByResponse2 | null | undefined,
): string | null => {
  if (!documentedBy) return null;
  const name = documentedBy.display_name?.trim();
  if (!name) return null;
  const role = documentedBy.role_label?.trim();
  return role ? `Documented by ${name} · ${role}` : `Documented by ${name}`;
};

/** "Documented by X · Signed by Y" for the completed bar and signature block. */
export const formatDocumentedAndSignedBy = (
  documentedBy: DocumentedByResponse2 | null | undefined,
  signerName: string | null | undefined,
): string | null => {
  const drafter = documentedBy?.display_name?.trim();
  const signer = signerName?.trim();
  if (!drafter) return null;
  return signer ? `Documented by ${drafter} · Signed by ${signer}` : `Documented by ${drafter}`;
};

export function useCosignContext(
  noteData: NoteResponse | undefined,
  encounterData?: Pick<EncounterResponse, 'provider_id'> | null,
): CosignContext {
  const { userState } = useUser();
  // ADR 0073 Phase 3 (GDN-1590): catalog parity with hasRole('supervisor')
  // (sign_supervised_notes) and hasBillingAccess (manage_billing).
  const isSupervisor = useCan('sign_supervised_notes');
  const isBillingAdmin = useCan('manage_billing');
  const encounterProviderId = encounterData?.provider_id ?? null;

  return useMemo<CosignContext>(() => {
    const currentUserId = userState?.user_id;
    const noteUserId = noteData?.issuing_user?.user_id;

    const isAuthor = !!(currentUserId && noteUserId && currentUserId === noteUserId);
    const needsCosign = !!noteData?.requires_cosigning;

    const completionStatus = noteData?.completion_status ?? '';
    const cosignStatus: CosignStatus = COSIGN_STATUSES.has(completionStatus)
      ? (completionStatus as CosignStatus)
      : 'none';

    const documentedBy = noteData?.documented_by ?? null;
    const isDocumenter = !!(
      currentUserId &&
      documentedBy?.user_id &&
      currentUserId === documentedBy.user_id
    );
    // Both sides must be known. An absent encounter must never read as a
    // match, or every viewer would look like the provider while it loads.
    const isEncounterProvider = !!(
      currentUserId &&
      encounterProviderId &&
      currentUserId === encounterProviderId
    );
    // Definitive when documented_by is present; presumptive on the legacy
    // shape. See the module docblock for why the presumption is safe here.
    const authorIsClinicalStaff = documentedBy !== null || needsCosign;

    return {
      isAuthor,
      isSupervisor,
      isBillingAdmin,
      needsCosign,
      cosignStatus,
      documentedBy,
      isDocumenter,
      isEncounterProvider,
      authorIsClinicalStaff,
    };
  }, [userState?.user_id, noteData, isSupervisor, isBillingAdmin, encounterProviderId]);
}
